import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChildren,
  QueryList,
  AfterViewChecked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { LucideAngularModule, Map as MapIcon, Pencil } from 'lucide-angular';
import {
  LessonBlock,
  LessonSlide,
  GreetingSlideContent,
  TextSlideContent,
  ListSlideContent,
  ByeSlideContent,
  SlideThumbnail,
} from '../../services/lesson-plan.models';

@Component({
  selector: 'app-lesson-map',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './lesson-map.html',
  styleUrl: './lesson-map.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LessonMapComponent implements AfterViewChecked {
  @ViewChildren('blockRef') blockRefs!: QueryList<ElementRef<HTMLDivElement>>;

  @Output() readonly slideSelected = new EventEmitter<{ slideIndex: number; blockId: number }>();
  @Output() readonly editRequested = new EventEmitter<void>();

  private readonly plan$$ = new BehaviorSubject<LessonBlock[]>([]);
  protected readonly plan$ = this.plan$$.asObservable();

  private readonly slides$$ = new BehaviorSubject<LessonSlide[]>([]);
  protected readonly slides$ = this.slides$$.asObservable();

  private readonly activeBlockId$$ = new BehaviorSubject<number>(1);
  protected readonly activeBlockId$ = this.activeBlockId$$.asObservable();

  private readonly activeSlideIndex$$ = new BehaviorSubject<number>(0);
  protected readonly activeSlideIndex$ = this.activeSlideIndex$$.asObservable();

  private readonly thumbnails$$ = new BehaviorSubject<SlideThumbnail[]>([]);
  protected readonly thumbnails$ = this.thumbnails$$.asObservable();

  protected readonly viewData$ = combineLatest([
    this.plan$,
    this.slides$,
    this.activeBlockId$,
    this.activeSlideIndex$,
    this.thumbnails$,
  ]).pipe(
    map(([plan, slides, activeBlockId, activeSlideIndex, thumbnails]) => ({
      plan,
      slides,
      activeBlockId,
      activeSlideIndex,
      thumbnails,
      slidesByBlock: this.groupSlidesByBlock(slides, plan),
    }))
  );

  private needsScroll = false;

  @Input()
  set plan(value: LessonBlock[]) {
    this.plan$$.next(value);
  }

  @Input()
  set slides(value: LessonSlide[]) {
    this.slides$$.next(value);
  }

  @Input()
  set activeBlockId(value: number) {
    const oldValue = this.activeBlockId$$.value;
    this.activeBlockId$$.next(value);
    if (oldValue !== value) {
      this.needsScroll = true;
    }
  }

  @Input()
  set activeSlideIndex(value: number) {
    this.activeSlideIndex$$.next(value);
  }

  @Input()
  set thumbnails(value: SlideThumbnail[]) {
    this.thumbnails$$.next(value || []);
  }

  private readonly mobileMode$$ = new BehaviorSubject<boolean>(false);
  protected readonly mobileMode$ = this.mobileMode$$.asObservable();

  @Input()
  set mobileMode(value: boolean) {
    this.mobileMode$$.next(value);
  }

  protected readonly icons = {
    Map: MapIcon,
    Edit: Pencil,
  };

  ngAfterViewChecked(): void {
    if (this.needsScroll) {
      this.scrollToActiveBlock();
      this.needsScroll = false;
    }
  }

  private scrollToActiveBlock(): void {
    const activeId = this.activeBlockId$$.value;
    const plan = this.plan$$.value;
    const index = plan.findIndex((b) => b.id === activeId);

    if (index >= 0 && this.blockRefs) {
      const refs = this.blockRefs.toArray();
      if (refs[index]) {
        refs[index].nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  /**
   * Group slides by block using the blockId property from each slide
   */
  private groupSlidesByBlock(slides: LessonSlide[], plan: LessonBlock[]): Map<number, { slide: LessonSlide; index: number }[]> {
    const map = new Map<number, { slide: LessonSlide; index: number }[]>();
    
    if (plan.length === 0 || slides.length === 0) {
      return map;
    }

    // Initialize map with empty arrays for each block
    plan.forEach(block => map.set(block.id, []));

    // Group slides by their blockId property
    slides.forEach((slide, index) => {
      const blockId = slide.blockId;
      
      // If the blockId exists in our plan, use it; otherwise fall back to first block
      if (map.has(blockId)) {
        const blockSlides = map.get(blockId)!;
        blockSlides.push({ slide, index });
      } else {
        // Fallback: add to first block if blockId doesn't match
        const firstBlockId = plan[0].id;
        const blockSlides = map.get(firstBlockId) || [];
        blockSlides.push({ slide, index });
        map.set(firstBlockId, blockSlides);
      }
    });

    return map;
  }

  protected onSlideClick(slideIndex: number, blockId: number, event: Event): void {
    event.stopPropagation();
    this.slideSelected.emit({ slideIndex, blockId });
  }

  protected getSlideIndex(slide: LessonSlide): number {
    return this.slides$$.value.indexOf(slide);
  }

  protected getBlockStartTime(plan: LessonBlock[], blockIndex: number): number {
    let startTime = 0;
    for (let i = 0; i < blockIndex; i++) {
      startTime += plan[i]?.duration || 0;
    }
    return startTime;
  }

  protected isSlideActive(slideIndex: number): boolean {
    return slideIndex === this.activeSlideIndex$$.value;
  }

  /**
   * Get display title for a slide based on its type and content
   */
  protected getSlideTitle(slide: LessonSlide): string {
    switch (slide.type) {
      case 'greeting': {
        const content = slide.content as GreetingSlideContent;
        return content.title || 'Opening';
      }
      case 'text': {
        const content = slide.content as TextSlideContent;
        return content.title || 'Concept';
      }
      case 'list': {
        const content = slide.content as ListSlideContent;
        return content.title || 'Key Points';
      }
      case 'bye': {
        return 'Closing';
      }
      default:
        return 'Slide';
    }
  }

  /**
   * Get thumbnail for a specific slide index
   */
  protected getThumbnailForSlide(slideIndex: number, thumbnails: SlideThumbnail[]): SlideThumbnail | null {
    return thumbnails.find(t => t.slideIndex === slideIndex) || null;
  }
}
